import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    ArrowDownLeft, ArrowUpRight, Clock, CheckCircle,
    XCircle, Plus, Minus, RefreshCw,
} from 'lucide-react';

/* ─── Helpers ─── */
function formatAmount(cents) {
    return `€${(cents / 100).toFixed(2)}`;
}

function formatDate(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString('en-IE', {
        day: 'numeric', month: 'short', year: 'numeric',
    });
}

function txIcon(type, status) {
    if (status === 'PENDING') return <Clock size={16} color="var(--amber)" />;
    if (status === 'FAILED')  return <XCircle size={16} color="#ef4444" />;
    if (type === 'DEPOSIT')   return <ArrowDownLeft size={16} color="var(--teal-700)" />;
    if (type === 'WITHDRAW')  return <ArrowUpRight size={16} color="#e07b54" />;
    return <ArrowDownLeft size={16} color="var(--teal-700)" />;  // TRANSFER received
}

function txSign(type) {
    if (type === 'WITHDRAW') return '-';
    if (type === 'DEPOSIT')  return '+';
    return '';  // TRANSFER — sign depends on description
}

function txColor(type, status) {
    if (status === 'FAILED')  return 'var(--ink-400)';
    if (type === 'WITHDRAW')  return '#b91c1c';
    if (type === 'DEPOSIT')   return 'var(--teal-700)';
    return 'var(--ink-900)';
}

/* ─── Deposit amount picker ─── */
const QUICK_AMOUNTS = [10, 25, 50, 100, 200, 500];

function DepositModal({ onClose }) {
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleDeposit = async () => {
        const val = parseFloat(amount);
        if (!val || val < 1) { setError('Enter at least €1'); return; }
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
            const res = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/stripe/create-checkout-session`,
                { amount: Math.round(val * 100) }, // pence/cents
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Use the Stripe-hosted URL returned by the backend — no Stripe.js needed
            const url = res.data.url || res.data.sessionUrl;
            if (!url) throw new Error('No checkout URL returned from server.');
            window.location.href = url;
        } catch (e) {
            setError('Failed to start checkout. Please try again.');
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(11,20,17,0.4)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
            <div style={{
                position: 'relative', background: 'var(--white)',
                borderRadius: 'var(--r-xl)', padding: '36px 32px',
                width: 'min(480px, 95vw)', boxShadow: 'var(--shadow-lg)',
                border: '1px solid var(--ink-200)',
            }}>
                <h2 style={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--ink-950)', margin: '0 0 6px' }}>
                    Add money
                </h2>
                <p className="ix-body" style={{ margin: '0 0 24px' }}>Paid securely by card via Stripe.</p>

                {/* Quick amounts */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px' }}>
                    {QUICK_AMOUNTS.map(q => (
                        <button
                            key={q}
                            className={`cj-pill${amount === String(q) ? ' is-active' : ''}`}
                            onClick={() => setAmount(String(q))}
                            style={{ justifyContent: 'center', borderRadius: 'var(--r-md)', padding: '12px' }}
                        >
                            €{q}
                        </button>
                    ))}
                </div>

                {/* Custom amount */}
                <div className="cj-pay-input" style={{ marginBottom: '8px' }}>
                    <span className="cj-pay-currency" style={{ fontSize: '22px' }}>€</span>
                    <input
                        type="number"
                        placeholder="0"
                        min="1"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        style={{ fontSize: '28px' }}
                        autoFocus
                    />
                </div>

                {error && <p style={{ color: '#b91c1c', fontSize: '13px', margin: '0 0 16px' }}>{error}</p>}

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button className="ix-btn ix-btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
                    <button
                        className="ix-btn ix-btn-primary"
                        style={{ flex: 2 }}
                        onClick={handleDeposit}
                        disabled={loading}
                    >
                        {loading ? 'Redirecting…' : `Add €${amount || '0'}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Withdrawal modal ─── */
function WithdrawModal({ balance, onClose, onSuccess }) {
    const [amount, setAmount] = useState('');
    const [iban, setIban] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleWithdraw = async () => {
        const val = parseFloat(amount);
        if (!val || val < 1)      { setError('Enter at least €1'); return; }
        if (val > balance)        { setError('Amount exceeds your balance'); return; }
        if (!iban.trim())         { setError('Enter your IBAN'); return; }
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/stripe/withdraw`,
                { amount: val, iban: iban.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            onSuccess();
            onClose();
        } catch (e) {
            const msg = e.response?.data;
            setError(typeof msg === 'string' ? msg : 'Withdrawal failed. Please try again.');
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(11,20,17,0.4)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
            <div style={{
                position: 'relative', background: 'var(--white)',
                borderRadius: 'var(--r-xl)', padding: '36px 32px',
                width: 'min(480px, 95vw)', boxShadow: 'var(--shadow-lg)',
                border: '1px solid var(--ink-200)',
            }}>
                <h2 style={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--ink-950)', margin: '0 0 6px' }}>
                    Withdraw funds
                </h2>
                <p className="ix-body" style={{ margin: '0 0 24px' }}>
                    Funds arrive in 1–3 business days via bank transfer.
                </p>

                {/* Amount */}
                <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--ink-700)', display: 'block', marginBottom: '6px' }}>
                    Amount (available: €{balance?.toFixed(2) ?? '0.00'})
                </label>
                <div className="cj-pay-input" style={{ marginBottom: '16px' }}>
                    <span className="cj-pay-currency" style={{ fontSize: '22px' }}>€</span>
                    <input
                        type="number"
                        placeholder="0"
                        min="1"
                        max={balance}
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        style={{ fontSize: '28px' }}
                        autoFocus
                    />
                </div>

                {/* IBAN */}
                <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--ink-700)', display: 'block', marginBottom: '6px' }}>
                    Your IBAN
                </label>
                <div className="cj-input" style={{ marginBottom: '8px' }}>
                    <input
                        type="text"
                        placeholder="IE29 AIBK 9311 5212 3456 78"
                        value={iban}
                        onChange={e => setIban(e.target.value.toUpperCase())}
                        style={{ fontSize: '15px', letterSpacing: '0.04em' }}
                    />
                </div>
                <p style={{ fontSize: '12px', color: 'var(--ink-500)', margin: '0 0 16px' }}>
                    Your IBAN is used only for this withdrawal and is not stored.
                </p>

                {error && <p style={{ color: '#b91c1c', fontSize: '13px', margin: '0 0 16px' }}>{error}</p>}

                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                    <button className="ix-btn ix-btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
                    <button
                        className="ix-btn ix-btn-primary"
                        style={{ flex: 2 }}
                        onClick={handleWithdraw}
                        disabled={loading}
                    >
                        {loading ? 'Submitting…' : 'Request withdrawal'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Main Wallet page ─── */
const Wallet = () => {
    const [balance, setBalance] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeposit, setShowDeposit] = useState(false);
    const [showWithdraw, setShowWithdraw] = useState(false);
    const [toast, setToast] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');

    const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 4000); };

    const load = async () => {
        if (!token) { navigate('/login'); return; }
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/stripe/balance`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setBalance(res.data.balance ?? 0);
            // Sort newest first
            const sorted = [...(res.data.transactions || [])].sort(
                (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
            );
            setTransactions(sorted);
        } catch {
            showToast('Failed to load wallet. Please refresh.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // Handle Stripe redirect back
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('deposit') === 'success') {
            showToast('Deposit successful! Your balance will update shortly.');
            // Reload after a moment to pick up the webhook-credited balance
            setTimeout(load, 2000);
        } else if (params.get('deposit') === 'cancelled') {
            showToast('Deposit cancelled.');
        }
    }, []);

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px', color: 'var(--ink-500)' }}>
            Loading wallet…
        </div>
    );

    return (
        <div style={{ background: 'var(--paper)', minHeight: '100vh', padding: '0 0 96px' }}>
            <div className="ix-container" style={{ paddingTop: '48px' }}>

                {/* Page header */}
                <header style={{ marginBottom: '40px' }}>
                    <span className="eyebrow" style={{ display: 'block', marginBottom: '12px' }}>Your money</span>
                    <h1 className="h-section">
                        Wallet
                    </h1>
                </header>

                {/* Balance card */}
                <div style={{
                    background: 'var(--ink-950)', color: '#fff',
                    borderRadius: 'var(--r-xl)', padding: '40px 44px',
                    marginBottom: '24px', position: 'relative', overflow: 'hidden',
                }}>
                    <div style={{
                        position: 'absolute', inset: 0, pointerEvents: 'none',
                        background: 'radial-gradient(60% 60% at 100% 0%, color-mix(in srgb, var(--teal-500) 30%, transparent) 0%, transparent 60%)',
                    }} />
                    <div style={{ position: 'relative' }}>
                        <div className="mono" style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
                            Available balance
                        </div>
                        <div style={{ fontSize: 'clamp(40px, 6vw, 72px)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1, marginBottom: '32px' }}>
                            €{(balance ?? 0).toFixed(2)}
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <button
                                className="ix-btn"
                                style={{ background: '#fff', color: 'var(--ink-950)', fontWeight: 600 }}
                                onClick={() => setShowDeposit(true)}
                            >
                                <Plus size={16} /> Add money
                            </button>
                            <button
                                className="ix-btn"
                                style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.24)' }}
                                onClick={() => setShowWithdraw(true)}
                            >
                                <Minus size={16} /> Withdraw
                            </button>
                            <button
                                className="ix-btn ix-btn-ghost"
                                style={{ color: 'rgba(255,255,255,0.6)', marginLeft: 'auto' }}
                                onClick={() => { setLoading(true); load(); }}
                                aria-label="Refresh"
                            >
                                <RefreshCw size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Info strip */}
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '40px',
                }}>
                    {[
                        { label: 'Deposits', note: 'Via card, instant' },
                        { label: 'Job payments', note: 'Auto on job done' },
                        { label: 'Withdrawals', note: '1–3 business days' },
                    ].map(item => (
                        <div key={item.label} style={{
                            background: 'var(--white)', border: '1px solid var(--ink-200)',
                            borderRadius: 'var(--r-lg)', padding: '16px 18px',
                        }}>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--ink-950)', marginBottom: '4px' }}>{item.label}</div>
                            <div className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)' }}>{item.note}</div>
                        </div>
                    ))}
                </div>

                {/* Transaction history */}
                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.015em', color: 'var(--ink-950)', margin: '0 0 16px' }}>
                        Transaction history
                    </h2>

                    {transactions.length === 0 ? (
                        <div style={{
                            background: 'var(--white)', border: '1px dashed var(--ink-200)',
                            borderRadius: 'var(--r-lg)', padding: '60px 24px', textAlign: 'center',
                            color: 'var(--ink-500)',
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.3 }}>€</div>
                            <div style={{ fontWeight: 500, color: 'var(--ink-700)', marginBottom: '4px' }}>No transactions yet</div>
                            <div className="ix-body">Deposit money to get started.</div>
                        </div>
                    ) : (
                        <div style={{
                            background: 'var(--white)', border: '1px solid var(--ink-200)',
                            borderRadius: 'var(--r-lg)', overflow: 'hidden',
                        }}>
                            {transactions.map((tx, i) => {
                                const isTransferIn = tx.type === 'TRANSFER' &&
                                    tx.description?.toLowerCase().includes('received');
                                const sign = tx.type === 'WITHDRAW' ? '-'
                                    : tx.type === 'DEPOSIT' ? '+'
                                    : isTransferIn ? '+' : '-';
                                const color = tx.status === 'FAILED' ? 'var(--ink-400)'
                                    : tx.type === 'WITHDRAW' ? '#b91c1c'
                                    : tx.type === 'DEPOSIT' ? 'var(--teal-700)'
                                    : isTransferIn ? 'var(--teal-700)' : '#e07b54';

                                return (
                                    <div key={tx.id || i} style={{
                                        display: 'grid', gridTemplateColumns: 'auto 1fr auto',
                                        alignItems: 'center', gap: '14px',
                                        padding: '16px 20px',
                                        borderBottom: i < transactions.length - 1 ? '1px solid var(--ink-100)' : 'none',
                                    }}>
                                        {/* Icon */}
                                        <div style={{
                                            width: '36px', height: '36px', borderRadius: '50%',
                                            background: 'var(--paper-2)', display: 'grid', placeItems: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {txIcon(tx.type, tx.status)}
                                        </div>

                                        {/* Label + date */}
                                        <div>
                                            <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--ink-950)', marginBottom: '2px' }}>
                                                {tx.description || tx.type}
                                            </div>
                                            <div className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)' }}>
                                                {formatDate(tx.timestamp)}
                                                {tx.status === 'PENDING' && (
                                                    <span style={{ marginLeft: '8px', color: 'var(--amber)' }}>Processing</span>
                                                )}
                                                {tx.status === 'FAILED' && (
                                                    <span style={{ marginLeft: '8px', color: '#ef4444' }}>Failed</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Amount */}
                                        <div style={{ fontSize: '16px', fontWeight: 600, color, letterSpacing: '-0.01em', textAlign: 'right' }}>
                                            {sign}{formatAmount(tx.amount)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {showDeposit && <DepositModal onClose={() => setShowDeposit(false)} />}
            {showWithdraw && (
                <WithdrawModal
                    balance={balance}
                    onClose={() => setShowWithdraw(false)}
                    onSuccess={() => {
                        showToast('Withdrawal request submitted. Funds arrive in 1–3 business days.');
                        load();
                    }}
                />
            )}

            {/* Toast */}
            {toast && (
                <div style={{
                    position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
                    background: 'var(--ink-950)', color: '#fff', padding: '12px 22px',
                    borderRadius: 'var(--r-pill)', fontSize: '14px',
                    fontFamily: 'var(--font-sans)', zIndex: 200,
                    boxShadow: 'var(--shadow-lg)', whiteSpace: 'nowrap',
                }}>
                    {toast}
                </div>
            )}
        </div>
    );
};

export default Wallet;
